import { useCallback } from "react";
import useSWR from "swr";

import { ModerationRulesetService } from "@/services/moderation-ruleset.service";

export interface ModerationRuleLabelMap {
  /** Human-readable rule name for a slug; undefined when unknown. */
  readonly labelFor: (slug: string) => string | undefined;
  readonly isLoading: boolean;
}

/**
 * Maps moderation rule slugs to their human-readable labels for one or more
 * global rulesets. Violations and passed-rule lists carry slugs plus the
 * canonical ruleset ID needed here.
 */
export function useModerationRuleLabelMap(
  rulesetIds: readonly (string | null | undefined)[],
): ModerationRuleLabelMap {
  const uniqueIds = [...new Set(rulesetIds.filter((id): id is string => Boolean(id)))].sort();
  const key = uniqueIds.length > 0
    ? (["moderation-rule-labels", ...uniqueIds] as const)
    : null;

  const { data, isLoading } = useSWR(
    key,
    async ([, ...scopeRulesetIds]): Promise<ReadonlyMap<string, string>> => {
      const responses = await Promise.all(
        scopeRulesetIds.map((rulesetId) => ModerationRulesetService.listRules(rulesetId)),
      );
      const labels = new Map<string, string>();
      for (const response of responses) {
        for (const rule of response) {
          labels.set(rule.slug, rule.name);
        }
      }
      return labels;
    },
    { revalidateOnFocus: false },
  );

  const labelFor = useCallback((slug: string) => data?.get(slug), [data]);

  return { labelFor, isLoading };
}
